import { logError } from '@/apis/log'
import jsQR from 'jsqr'

export const Scanner = jsQR

export const scanImage = (imageBuffer: Uint8ClampedArray, width: number, height: number) => {
  const code = jsQR(imageBuffer, width, height)
  return code
}

interface QrScannerArgument {
  video: HTMLVideoElement
  callback: (code: string) => void
  period?: number
  discoveryCount?: number
  throttleLimit?: number
}

export class QrScanner {
  private static MIN_SCHEDULE_DELAY = 100
  private videoTickTimer: ReturnType<typeof setTimeout> | null = null
  private videoTickTimerHandler: () => void
  private hiddenCanvas: HTMLCanvasElement = document.createElement('canvas')
  private lastFoundQrCode: string | null = null
  private lastFoundSameQrCodeCount = 1
  private lastFoundAt: number | null = null
  private schedulePeriod = 500
  private isInitialized = false
  private canvasCtx: CanvasRenderingContext2D | null = null
  private baseSize = 0
  private isReleased = false

  constructor(private args: QrScannerArgument) {
    this.videoTickTimerHandler = () => {
      const startAt = Date.now()
      this.scanAndFindQrCode()
      const current = Date.now()
      let nextScheduledAt = startAt + this.schedulePeriod - current

      if (nextScheduledAt <= QrScanner.MIN_SCHEDULE_DELAY) {
        nextScheduledAt = this.schedulePeriod
      }

      if (!this.isReleased) {
        this.videoTickTimer = setTimeout(this.videoTickTimerHandler, nextScheduledAt)
      }
    }
  }

  start(): boolean {
    if (!this.isInitialized) {
      this.setupVariables()
      this.loadCanvasForCapture()
      this.isInitialized = true
    }

    this.stopTimer()

    if (this.isCameraStarted()) {
      this.videoTickTimer = setTimeout(this.videoTickTimerHandler, this.schedulePeriod)
      return true
    } else {
      return false
    }
  }

  stop() {
    this.stopTimer()
  }

  release() {
    this.isReleased = true
    this.stopTimer()
    if (this.hiddenCanvas && this.hiddenCanvas.parentNode) {
      this.hiddenCanvas.parentNode.removeChild(this.hiddenCanvas)
    }
  }

  private setupVariables() {
    this.args.discoveryCount = this.args.discoveryCount || 0
    this.args.throttleLimit = this.args.throttleLimit || 0

    if (this.args.period) {
      this.schedulePeriod = this.args.period
    }

    this.baseSize = Math.min(this.args.video.videoWidth, this.args.video.videoHeight, 450) || 0
  }

  private loadCanvasForCapture() {
    this.hiddenCanvas.style.display = 'none'
    this.hiddenCanvas.setAttribute('width', `${this.baseSize}`)
    this.hiddenCanvas.setAttribute('height', `${this.baseSize}`)
    document.body.appendChild(this.hiddenCanvas)

    this.canvasCtx = this.hiddenCanvas.getContext('2d')

    if (this.canvasCtx) {
      this.canvasCtx.imageSmoothingEnabled = false
    }
  }

  private scanAndFindQrCode() {
    const { video, callback, discoveryCount, throttleLimit } = this.args

    if (this.canvasCtx != null) {
      const baseSize = this.baseSize
      const [centerX, centerY] = [Math.floor(video.videoWidth / 2), Math.floor(video.videoHeight / 2)]
      const top = centerY - Math.floor(baseSize / 2)
      const left = centerX - Math.floor(baseSize / 2)

      this.canvasCtx.drawImage(video, left, top, baseSize, baseSize, 0, 0, this.baseSize, this.baseSize)

      let frame: ImageData | null = null

      try {
        frame = this.canvasCtx.getImageData(0, 0, baseSize, baseSize)!
      } catch (e: any) {
        logError({
          error: 'QR_GET_IMAGE_DATA_ERROR',
          message: e,
        })
        return
      }

      const code = jsQR(frame.data, frame.width, frame.height)

      if (code && code.data) {
        const qrCode = code.data
        const current = Date.now()

        if (throttleLimit && this.lastFoundAt && current > this.lastFoundAt + throttleLimit) {
          this.lastFoundQrCode = null
          this.lastFoundSameQrCodeCount = 1
          this.lastFoundAt = null
        }

        if (this.lastFoundQrCode !== qrCode) {
          this.lastFoundQrCode = qrCode
        } else {
          this.lastFoundSameQrCodeCount++
        }

        if (!discoveryCount || this.lastFoundSameQrCodeCount === discoveryCount) {
          this.lastFoundAt = current
          try {
            callback(code.data)
          } catch (e: any) {
            logError({
              error: 'QR_CALLBACK_ERROR',
              message: code.data,
            })
          }
        }
      }
    }
  }

  private stopTimer() {
    if (this.videoTickTimer != null) {
      clearTimeout(this.videoTickTimer)
      this.videoTickTimer = null
    }
  }

  public isCameraStarted() {
    return this.args.video.videoWidth > 0 && this.args.video.videoHeight > 0
  }
}
